import { lazy, Suspense } from "react"
import { Routes, Route } from "react-router-dom"
import { Alert } from "react-bootstrap"
import ErrorBoundary from "./ErrorBoundary"
import LoadingMessage from "./LoadingMessage"
import useAuthenticationContext from "../authentication/AuthenticationContext"
import useIsZone from "../../hooks/useIsZone"
import { useTenantSubDomain } from "../tenancy/TenantHooks"

const Assessments = lazy(() => import("../assessments/Assessments"))
const Dashboard = lazy(() => import("../dashboard/Dashboard"))
const DevelopmentTools = lazy(() => import("../DevelopmentTools"))
const BulkAddProfessionals = lazy(
	() => import("../professionals/bulkAdd/BulkAdd")
)
const ConsultantEmails = lazy(() => import("../extracts/ConsultantEmails"))
const NhsEnglandExtract = lazy(() => import("../extracts/NhsEnglandExtract"))
const Phin = lazy(() => import("../extracts/Phin"))
const TenantSpecificExtract = lazy(
	() => import("../extracts/TenantSpecificExtract")
)
const AddProfessional = lazy(() => import("../professionals/AddProfessional"))
const ShowProfessional = lazy(() => import("../professionals/ShowProfessional"))
const AssessmentsWithIssuesList = lazy(
	() =>
		import("../patients/conditions/investigator/AssessmentsWithIssuesList")
)
const PatientConditionInvestigator = lazy(
	() =>
		import("../patients/conditions/investigator/PatientConditionInvestigator")
)
const EventLog = lazy(() => import("../patients/EventLog"))
const SearchProfessionals = lazy(
	() => import("../professionals/SearchProfessionals")
)
const SearchPatients = lazy(() => import("../patients/SearchPatients"))
const ShowPatient = lazy(() => import("../patients/ShowPatient"))
const TransferPatientCondition = lazy(
	() =>
		import("../patients/TransferPatientCondition/TransferPatientCondition")
)
const BulkAddShares = lazy(() => import("../professionals/BulkAddShares"))
const BridgeRecords = lazy(() => import("../integrations/BridgeRecords"))
const GenesisCareBridgeData = lazy(
	() => import("../integrations/Tenant/GenesisCare/GenesisCareBridgeData")
)
const GenesisCareIntegrationTestApi = lazy(
	() =>
		import("../integrations/Tenant/GenesisCare/GenesisCareIntegrationTestApi")
)
const SpireBridgeData = lazy(
	() => import("../integrations/Tenant/Spire/SpireBridgeData")
)
const SpireIntegrationTestApi = lazy(
	() => import("../integrations/Tenant/Spire/SpireIntegrationTestApi")
)
const BulkShare = lazy(() => import("../professionals/BulkShare/BulkShare"))
const FakeData = lazy(() => import("../patients/fake/FakeData"))
const ConfigSearch = lazy(() => import("../ConfigSearch/ConfigSearch"))
const EmailPreview = lazy(() => import("../templates/EmailPreview"))
const SmsPreview = lazy(() => import("../templates/SmsPreview"))
const Audit = lazy(() => import("../audit/Audit"))
const ProfessionalSso = lazy(() => import("../client/ProfessionalSso"))
const NhsNumberImport = lazy(() => import("../patients/NhsNumberImport"))
const UnpackTransformMerge = lazy(
	() => import("../integrations/UnpackTransformMerge")
)
const Decommission = lazy(() => import("../decommission/Decommission"))
const StateMachines = lazy(() => import("../stateMachines/StateMachines"))

const DevToolsWarning = () => (
	<Alert variant="warning" className="my-3">
		<h3>Development Tools not available in production</h3>
	</Alert>
)

const AuthenticatedRoutes = () => {
	const { isDevelopment, isTest, isProduction } = useIsZone()
	const tenantSubDomain = useTenantSubDomain()
	const {
		credentialDetails: { isAuthenticated }
	} = useAuthenticationContext()

	if (!isAuthenticated) {
		return null
	}

	return (
		<Suspense fallback={<LoadingMessage>Loading</LoadingMessage>}>
			<ErrorBoundary
				errorPage={() => (
					<h1>
						Something has gone wrong. Try force refresh CTRL-F5 or
						Command-Shift-R. Failing that, let tech team know in
						Slack.
					</h1>
				)}
			>
				<Routes>
					<Route path="professionals/*">
						<Route path={`add`} element={<AddProfessional />} />
						<Route
							path={`bulkadd`}
							element={<BulkAddProfessionals />}
						/>
						<Route
							path={`sharing/bulkadd`}
							element={<BulkAddShares />}
						/>
						<Route
							path={`sharing/bulkshare`}
							element={<BulkShare />}
						/>
						<Route
							path={`:userId`}
							element={<ShowProfessional />}
						/>
						<Route
							path={`search`}
							element={<SearchProfessionals />}
						/>
					</Route>
					<Route path="patients/*">
						<Route path={`:userId`} element={<ShowPatient />} />
						<Route
							path={`transfer/:patientConditionUuid`}
							element={<TransferPatientCondition />}
						/>
						<Route path={`search`} element={<SearchPatients />} />
						<Route
							path={`nhsNumbers`}
							element={<NhsNumberImport />}
						/>
						<Route
							path={`issues`}
							element={<AssessmentsWithIssuesList />}
						/>
						<Route
							path={`issues/:patientConditionId`}
							element={<PatientConditionInvestigator />}
						/>
						<Route path={`events`} element={<EventLog />} />
					</Route>
					<Route path={`audit`} element={<Audit />} />
					<Route
						path={`extracts/consultant-emails`}
						element={<ConsultantEmails />}
					/>
					<Route
						path={`extracts/nhsengland`}
						element={<NhsEnglandExtract />}
					/>
					<Route path={`extracts/phin`} element={<Phin />} />
					<Route
						path={`extracts/tenant`}
						element={<TenantSpecificExtract />}
					/>
					<Route
						path={`templates/emails/preview`}
						element={<EmailPreview />}
					/>
					<Route
						path={`integrations/bridge-records`}
						element={<BridgeRecords />}
					/>
					<Route
						path={`integrations/unpack-transform-merge`}
						element={<UnpackTransformMerge />}
					/>
					{tenantSubDomain === "genesiscare" && (
						<Route
							path={`integrations/genesis-care-bridge-data`}
							element={<GenesisCareBridgeData />}
						/>
					)}
					{(isDevelopment || isTest) &&
						tenantSubDomain === "genesiscare" && (
							<Route
								path={`integrations/genesis-care-test-api`}
								element={<GenesisCareIntegrationTestApi />}
							/>
						)}
					{tenantSubDomain === "spire" && (
						<Route
							path={`integrations/spire-bridge-data`}
							element={<SpireBridgeData />}
						/>
					)}
					{(isDevelopment || isTest) &&
						tenantSubDomain === "spire" && (
							<Route
								path={`integrations/spire-test-api`}
								element={<SpireIntegrationTestApi />}
							/>
						)}
					<Route
						path={`templates/sms/preview`}
						element={<SmsPreview />}
					/>
					<Route
						path={`client/sso/professional`}
						element={<ProfessionalSso />}
					/>
					<Route
						path={`devtools`}
						element={
							isProduction ? (
								<DevToolsWarning />
							) : (
								<DevelopmentTools />
							)
						}
					/>
					<Route path={`assessments`} element={<Assessments />} />
					<Route path={`decommission`} element={<Decommission />} />
					<Route path={`states`} element={<StateMachines />} />
					<Route
						path={`fakedata`}
						element={
							isProduction ? <DevToolsWarning /> : <FakeData />
						}
					/>
					<Route path={`config-search`} element={<ConfigSearch />} />
					<Route path={`*`} element={<Dashboard />} />
				</Routes>
			</ErrorBoundary>
		</Suspense>
	)
}

export default AuthenticatedRoutes
